// components/EnhancedClientDashboard.js
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import EnhancedWithdrawalRequest from "../Dashboards/WithdrawalRequest";
import {
  getFirestore,
  doc,
  onSnapshot,
} from "firebase/firestore";
import { useAuth } from "../Authcontext";

import {
  // Enhanced functions
  getUserSettings,
  calculateDepositStatsEnhanced,
  calculatePortfolioTotalsEnhanced,
  subscribeToUserDeposits,
  calculateDailyRate,
  getDaysElapsed,
  // Legacy functions for fallback
  calculateDepositStats,
  calculatePortfolioTotals,
} from "../utils/growthCalculations";
import { assignReferralCode } from "../utils/referralSystem";
import AddReferralCodeEnhanced from './AddReferralCodeEnhanced';
const firestore = getFirestore();

// Enhanced Stat Card Component
const StatCard = ({ title, value, icon, trend, color = "blue", subtitle }) => (
  <motion.div
    className="bg-white/15 backdrop-blur-md rounded-xl p-4 transition-all duration-300 hover:bg-white/20 hover:transform hover:scale-105 shadow-lg border border-white/10"
    whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
    whileTap={{ scale: 0.95, transition: { duration: 0.1 } }}
  >
    <div className="flex items-center justify-between mb-2">
      <motion.span
        className={`text-${color}-400 text-xl`}
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0, transition: { delay: 0.1 } }}
      >
        {icon}
      </motion.span>
      {trend !== undefined && (
        <motion.span
          className={`text-sm px-2 py-1 rounded ${
            trend > 0 ? "text-green-400 bg-green-400/20" : "text-red-400 bg-red-400/20"
          }`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { delay: 0.2 } }}
        >
          {trend > 0 ? "↑" : "↓"} {Math.abs(trend).toFixed(2)}%
        </motion.span>
      )}
    </div>
    <h3 className="text-gray-400 text-sm font-medium">{title}</h3>
    <motion.p
      className="text-white text-lg font-bold mt-1"
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0, transition: { delay: 0.3 } }}
    >
      {value}
    </motion.p>
    {subtitle && (
      <p className="text-gray-500 text-xs mt-1">{subtitle}</p>
    )}
  </motion.div>
);

// Enhanced Deposit Card Component
const DepositCard = ({ deposit, userSettings, currency, userId }) => {
  const [stats, setStats] = useState({
    currentValue: deposit.amount,
    totalGain: 0,
    gainPercentage: 0,
    dailyGain: 0,
    appliedRate: userSettings?.monthlyRate || 10,
    isPromoRate: false,
    hasReferralBonus: false
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const updateStats = async () => {
      if (deposit.status === 'confirmed') {
        try {
          // Try enhanced calculation first
          const enhancedStats = await calculateDepositStatsEnhanced(deposit, userId);
          setStats(enhancedStats);
        } catch (error) {
          console.error("Enhanced calculation failed, falling back to legacy:", error);
          // Fallback to legacy calculation
          const legacyStats = calculateDepositStats(deposit, userSettings?.monthlyRate || 10);
          setStats({
            ...legacyStats,
            appliedRate: userSettings?.monthlyRate || 10,
            isPromoRate: false,
            hasReferralBonus: false
          });
        }
      }
      setIsLoading(false);
    };

    updateStats();
    const interval = setInterval(updateStats, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [deposit, userSettings, userId]);

  const daysElapsed = getDaysElapsed(deposit.createdAt);
  const depositDate = deposit.createdAt?.toDate?.() || new Date(deposit.createdAt);

  return (
    <motion.div
      className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20 hover:bg-white/15 transition-all duration-300"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      whileHover={{ scale: 1.02 }}
    >
      <div className="flex justify-between items-start mb-3">
        <div>
          <p className="text-sm text-gray-400">Deposit #{deposit.id.slice(-6)}</p>
          <p className="text-xs text-gray-500">
            {depositDate.toLocaleDateString()} ({daysElapsed} days ago)
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={`text-xs px-2 py-1 rounded ${
            deposit.status === 'confirmed' 
              ? 'bg-green-500/20 text-green-400' 
              : deposit.status === 'pending'
              ? 'bg-yellow-500/20 text-yellow-400'
              : 'bg-red-500/20 text-red-400'
          }`}>
            {deposit.status}
          </span>
          {stats.isPromoRate && (
            <span className="text-xs px-2 py-1 rounded bg-purple-500/20 text-purple-400">
              PROMO
            </span>
          )}
          {stats.hasReferralBonus && (
            <span className="text-xs px-2 py-1 rounded bg-yellow-500/20 text-yellow-400">
              BONUS
            </span>
          )}
        </div>
      </div>
      
      <div className="space-y-2">
        <div className="flex justify-between">
          <p className="text-xs text-gray-400">Principal</p>
          <p className="text-white font-semibold">
            {currency} {deposit.amount.toLocaleString()}
          </p>
        </div>
        
        {deposit.status === 'confirmed' && !isLoading && (
          <>
            <div className="flex justify-between">
              <p className="text-xs text-gray-400">Applied Rate</p>
              <p className="text-blue-400 font-semibold">
                {stats.appliedRate.toFixed(1)}%/month
              </p>
            </div>
            
            <div className="flex justify-between">
              <p className="text-xs text-gray-400">Current Value</p>
              <p className="text-white font-bold">
                {currency} {stats.currentValue.toFixed(2).toLocaleString()}
              </p>
            </div>
            
            <div className="flex justify-between">
              <p className="text-xs text-gray-400">Total Gain</p>
              <p className={`font-semibold ${stats.totalGain > 0 ? 'text-green-400' : 'text-gray-400'}`}>
                +{currency} {stats.totalGain.toFixed(2)} ({stats.gainPercentage.toFixed(2)}%)
              </p>
            </div>
            
            <div className="flex justify-between">
              <p className="text-xs text-gray-400">Daily Avg</p>
              <p className="text-blue-400 text-sm">
                {currency} {stats.dailyGain.toFixed(2)}/day
              </p>
            </div>
          </>
        )}
        
        {isLoading && deposit.status === 'confirmed' && (
          <div className="flex justify-center py-2">
            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-400"></div>
          </div>
        )}
        
        {deposit.description && (
          <div className="mt-2 pt-2 border-t border-white/10">
            <p className="text-xs text-gray-400">{deposit.description}</p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// FIXED Personal Rate Display Component
const PersonalRateCard = ({ userSettings }) => {
  if (!userSettings) return null;

  const effectiveRate = userSettings.monthlyRate + (userSettings.referralBonus || 0);
  const dailyRate = calculateDailyRate(effectiveRate);
  
  // REMOVED the stray component call that was causing issues

  return (
    <motion.div 
      className="bg-gradient-to-r from-blue-500/20 to-purple-600/20 backdrop-blur-md rounded-xl p-6 border border-blue-500/30"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.15 }}
    >
      <h3 className="text-xl font-bold text-white mb-4 flex items-center">
        <span className="mr-2">⭐</span>
        Your Personal Investment Rate
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white/10 p-4 rounded-lg">
          <h4 className="text-blue-300 font-semibold text-sm">Base Monthly Rate</h4>
          <p className="text-white text-2xl font-bold">
            {userSettings.monthlyRate}%
            {userSettings.isUsingGlobalRate && (
              <span className="text-sm text-blue-300 ml-1">(Global)</span>
            )}
          </p>
        </div>
        
        {userSettings.referralBonus > 0 && (
          <div className="bg-white/10 p-4 rounded-lg">
            <h4 className="text-green-300 font-semibold text-sm">Referral Bonus</h4>
            <p className="text-green-400 text-2xl font-bold">+{userSettings.referralBonus}%</p>
            <p className="text-green-300 text-xs">{userSettings.totalReferrals} referrals</p>
          </div>
        )}
        
        <div className="bg-white/10 p-4 rounded-lg">
          <h4 className="text-purple-300 font-semibold text-sm">Effective Rate</h4>
          <p className="text-purple-400 text-2xl font-bold">{effectiveRate.toFixed(1)}%</p>
          <p className="text-purple-300 text-xs">{(dailyRate * 100).toFixed(4)}% daily</p>
        </div>
      </div>
      
      {userSettings.promoRates && userSettings.promoRates.length > 0 && (
        <div className="mt-4 p-3 bg-purple-500/20 rounded-lg border border-purple-500/30">
          <h4 className="text-purple-300 font-semibold text-sm mb-2">🎉 Active Promotions</h4>
          <div className="space-y-1">
            {userSettings.promoRates.map((promo, index) => (
              <div key={index} className="flex justify-between text-sm">
                <span className="text-purple-200">{promo.name}</span>
                <span className="text-purple-400 font-semibold">{promo.rate}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

// FIXED Referral Code Display Component - now with proper props
const ReferralCodeCard = ({ userDetails, currentUser, userSettings }) => {
  const [referralCode, setReferralCode] = useState(userDetails?.referralCode || null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  // Auto-generate code if user doesn't have one
  useEffect(() => {
    const autoGenerateCode = async () => {
      if (!referralCode && userDetails?.fullName && !isGenerating) {
        setIsGenerating(true);
        try {
          const code = await assignReferralCode(currentUser.uid, userDetails.fullName);
          setReferralCode(code);
          setError(null);
        } catch (error) {
          console.error("Error auto-generating referral code:", error);
          setError("Failed to generate referral code");
        } finally {
          setIsGenerating(false);
        }
      }
    };

    if (userDetails && currentUser) {
      autoGenerateCode();
    }
  }, [userDetails, currentUser, referralCode, isGenerating]);

  // Update referral code when userDetails changes
  useEffect(() => {
    if (userDetails?.referralCode && userDetails.referralCode !== referralCode) {
      setReferralCode(userDetails.referralCode);
    }
  }, [userDetails?.referralCode, referralCode]);

  const generateCode = async () => {
    if (!userDetails?.fullName) {
      setError("Full name is required to generate referral code");
      return;
    }

    setIsGenerating(true);
    setError(null);
    try {
      const code = await assignReferralCode(currentUser.uid, userDetails.fullName);
      setReferralCode(code);
    } catch (error) {
      console.error("Error generating referral code:", error);
      setError("Failed to generate referral code. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareUrl = referralCode ? `${window.location.origin}/register?ref=${referralCode}` : '';

  return (
    <motion.div 
      className="bg-gradient-to-r from-green-500/20 to-blue-600/20 backdrop-blur-md rounded-xl p-6 border border-green-500/30"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.25 }}
    >
      <h3 className="text-xl font-bold text-white mb-4 flex items-center">
        <span className="mr-2">🎁</span>
        Refer Friends & Earn Bonuses
      </h3>
      
      {error && (
        <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 text-red-300 rounded">
          {error}
        </div>
      )}
      
      {referralCode ? (
        <div className="space-y-4">
          <div className="bg-white/10 p-4 rounded-lg">
            <p className="text-gray-300 text-sm mb-2">Your Referral Code:</p>
            <div className="flex items-center gap-3">
              <code className="bg-black/30 text-green-300 px-4 py-2 rounded text-lg font-mono">
                {referralCode}
              </code>
              <button
                onClick={() => copyToClipboard(referralCode)}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
              >
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
          </div>
          
          <div className="bg-white/10 p-4 rounded-lg">
            <p className="text-gray-300 text-sm mb-2">Share this link:</p>
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={shareUrl}
                readOnly
                className="flex-1 bg-black/30 text-white px-3 py-2 rounded text-sm"
              />
              <button
                onClick={() => copyToClipboard(shareUrl)}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
              >
                Share
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white/10 p-3 rounded-lg text-center">
              <p className="text-green-300 text-lg font-bold">
                {userDetails?.referralStats?.totalReferrals || 0}
              </p>
              <p className="text-gray-300 text-sm">Total Referrals</p>
            </div>
            <div className="bg-white/10 p-3 rounded-lg text-center">
              <p className="text-blue-300 text-lg font-bold">
                {userDetails?.referralStats?.successfulReferrals || 0}
              </p>
              <p className="text-gray-300 text-sm">Active Referrals</p>
            </div>
            <div className="bg-white/10 p-3 rounded-lg text-center">
              <p className="text-purple-300 text-lg font-bold">
                +{userSettings?.referralBonus || 0}%
              </p>
              <p className="text-gray-300 text-sm">Bonus Rate</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center">
          <p className="text-gray-300 mb-4">Get your unique referral code and start earning bonuses!</p>
          <button
            onClick={generateCode}
            disabled={isGenerating || !userDetails?.fullName}
            className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50"
          >
            {isGenerating ? "Generating..." : "Get Referral Code"}
          </button>
          {!userDetails?.fullName && (
            <p className="text-red-300 text-sm mt-2">Full name required to generate referral code</p>
          )}
        </div>
      )}
    </motion.div>
  );
};

// Main Enhanced Client Dashboard Component
const EnhancedClientDashboard = () => {
  const [deposits, setDeposits] = useState([]);
  const [userDetails, setUserDetails] = useState(null);
  const [userSettings, setUserSettings] = useState(null);
  const [portfolioTotals, setPortfolioTotals] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showWithdrawalForm, setShowWithdrawalForm] = useState(false);
  const [withdrawalMessage, setWithdrawalMessage] = useState("");
  const { currentUser } = useAuth();
  
  // ADD THIS NEW HANDLER FUNCTION HERE:
  const handleReferralAdded = async () => {
    // Refresh user settings to get updated referral bonus
    try {
      const settings = await getUserSettings(currentUser.uid);
      setUserSettings(settings);
      // The real-time listener will automatically update userDetails
    } catch (error) {
      console.error("Error refreshing user settings:", error);
    }
  };
  // Fetch user settings and details
  useEffect(() => {
    if (!currentUser?.uid) return;

    const fetchUserData = async () => {
      try {
        // Get user settings (personalized rates)
        const settings = await getUserSettings(currentUser.uid);
        setUserSettings(settings);

        // Get user details with real-time updates
        const userDocRef = doc(firestore, "Investors", currentUser.uid);
        const unsubscribe = onSnapshot(userDocRef, (doc) => {
          if (doc.exists()) {
            const userData = doc.data();
            setUserDetails(userData);
          } else {
            setError("User details not found");
          }
        });

        return unsubscribe;
      } catch (error) {
        console.error("Error fetching user data:", error);
        setError("Failed to load user data");
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [currentUser]);

  // Subscribe to user deposits and calculate enhanced portfolio
  useEffect(() => {
    if (!currentUser?.uid || !userSettings) return;

    const unsubscribe = subscribeToUserDeposits(currentUser.uid, async (depositsData) => {
      setDeposits(depositsData);
      
      try {
        // Calculate enhanced portfolio totals
        const enhancedTotals = await calculatePortfolioTotalsEnhanced(depositsData, currentUser.uid);
        setPortfolioTotals(enhancedTotals);
      } catch (error) {
        console.error("Enhanced portfolio calculation failed, using legacy:", error);
        // Fallback to legacy calculation
        const legacyTotals = calculatePortfolioTotals(depositsData, userSettings.monthlyRate);
        setPortfolioTotals(legacyTotals);
      }
      
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser, userSettings]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-white mt-4">Loading your enhanced portfolio...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-center text-red-400">
          <p className="text-lg">Error: {error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen w-full overflow-hidden font-sans">
      {/* Video Background */}
      <video
        className="absolute top-0 left-0 w-full h-full object-cover"
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
      >
        <source src="https://res.cloudinary.com/adiec7/video/upload/v1728749526/3%E6%9C%8811%E6%97%A5_2_.mov.mp4" type="video/mp4" />
      </video>

      {/* Gradient Overlay */}
      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-black/80 via-black/70 to-black/80" />
      <div className="absolute top-0 left-0 w-full h-full bg-black/40" />

      {/* Content */}
      <div className="relative z-10 h-screen overflow-y-auto">
        <div className="max-w-7xl mx-auto p-6 pt-24 pb-24">
          <div className="space-y-6">
            {/* Welcome Message */}
            {userDetails && userSettings && (
              <motion.div 
                className="mb-6"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <h1 className="text-3xl font-bold text-white">
                  Welcome back, {userDetails.fullName}! 👋
                </h1>
                <p className="text-gray-400 mt-2">
                  Your investments are growing at your personalized rate of {userSettings.monthlyRate + (userSettings.referralBonus || 0)}% monthly
                  ({(calculateDailyRate(userSettings.monthlyRate + (userSettings.referralBonus || 0)) * 100).toFixed(4)}% daily)
                </p>
              </motion.div>
            )}

            {/* Personal Rate Information */}
            <PersonalRateCard userSettings={userSettings} />
             

             <AddReferralCodeEnhanced 
              userId={currentUser?.uid}
              userDetails={userDetails}
              onSuccess={handleReferralAdded}
            />
            {/* PROPERLY PLACED Referral Code Card with all required props */}
            <ReferralCodeCard 
              userDetails={userDetails} 
              currentUser={currentUser} 
              userSettings={userSettings}
            />

            {/* Stats Overview */}
            {portfolioTotals && (
              <motion.div 
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <StatCard
                  title="Total Invested"
                  value={`${userDetails?.currency || '$'} ${portfolioTotals.totalInvested.toLocaleString()}`}
                  icon="💰"
                  color="blue"
                  subtitle={`${portfolioTotals.confirmedDepositsCount} active deposits`}
                />
                <StatCard
                  title="Current Value"
                  value={`${userDetails?.currency || '$'} ${portfolioTotals.totalCurrentValue.toFixed(2).toLocaleString()}`}
                  icon="💎"
                  color="green"
                  subtitle="Enhanced calculation"
                />
                <StatCard
                  title="Total Gain"
                  value={`${userDetails?.currency || '$'} ${portfolioTotals.totalGain.toFixed(2).toLocaleString()}`}
                  icon="📈"
                  trend={portfolioTotals.totalGainPercentage}
                  color="purple"
                  subtitle={`${portfolioTotals.totalGainPercentage.toFixed(2)}% ROI`}
                />
                <StatCard
                  title="Avg Daily Gain"
                  value={`${userDetails?.currency || '$'} ${portfolioTotals.averageDailyGain?.toFixed(2) || '0.00'}`}
                  icon="⚡"
                  color="orange"
                  subtitle="Personalized rate"
                />
              </motion.div>
            )}

            {/* Manage Funds Section */}
            <motion.div 
              className="bg-white/15 backdrop-blur-md rounded-xl p-6 transition-all duration-300 hover:bg-white/20 shadow-lg border border-white/10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <h2 className="text-xl font-bold text-white mb-4">💳 Manage Funds</h2>
              
              {withdrawalMessage && (
                <div className="mb-4 p-3 bg-green-500/20 border border-green-500/50 text-green-300 rounded">
                  {withdrawalMessage}
                </div>
              )}
              
              <p className="text-gray-300 mb-4">
                Add new deposits or request withdrawals with your personalized rate calculations:
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <a href={`https://wa.me/2347026976893?text=${encodeURIComponent("Hello, I would like to add funds to my investment account.")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center px-4 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-green-300"
                >
                  <span className="mr-2">➕</span>
                  Add Funds
                </a>
                
                <button
                  onClick={() => setShowWithdrawalForm(true)}
                  disabled={!portfolioTotals || portfolioTotals.totalCurrentValue <= 0}
                  className="flex-1 inline-flex items-center justify-center px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="mr-2">💸</span>
                  Request Withdrawal
                </button>
              </div>

              {userSettings && (
                <div className="mt-4 p-3 bg-blue-500/20 rounded-lg border border-blue-500/30">
                  <p className="text-blue-300 text-sm">
                    💡 <strong>Enhanced Features:</strong> Your withdrawals are calculated using your personalized rate
                    {userSettings.referralBonus > 0 && " including your referral bonuses"}
                    {userSettings.promoRates?.length > 0 && " and active promotions"}.
                  </p>
                </div>
              )}
            </motion.div>

            {/* Deposits Section */}
            <motion.div 
              className="bg-white/5 backdrop-blur-md rounded-xl p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <h2 className="text-xl font-bold text-white mb-4">📊 Your Investment Deposits</h2>
              {deposits.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">💼</div>
                  <p className="text-gray-400 text-lg">No deposits yet</p>
                  <p className="text-gray-500 text-sm mt-2">
                    Contact admin to make your first investment and start earning at your personalized rate!
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {deposits.map((deposit) => (
                    <DepositCard
                      key={deposit.id}
                      deposit={deposit}
                      userSettings={userSettings}
                      currency={userDetails?.currency || '$'}
                      userId={currentUser.uid}
                    />
                  ))}
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>

      {/* Enhanced Withdrawal Modal */}
      {showWithdrawalForm && (
        <EnhancedWithdrawalRequest
          userDetails={userDetails}
          deposits={deposits}
          onClose={() => setShowWithdrawalForm(false)}
          onSuccess={(message) => {
            setWithdrawalMessage(message);
            setTimeout(() => setWithdrawalMessage(""), 5000);
          }}
        />
      )}
    </div>
  );
};

export default EnhancedClientDashboard;